'use client'

import { createContext, useContext, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Menu } from 'lucide-react'
import { Sidebar } from './Sidebar'
import { usePersistedState } from '@/lib/usePersistedState'

export function useSidebarCollapsed() {
  return usePersistedState<boolean>('sidebar-collapsed', false);
}

const MobileNavContext = createContext<{ open: boolean; setOpen: (v: boolean) => void }>({ open: false, setOpen: () => {} });
export function useMobileNav() { return useContext(MobileNavContext); }

export function LayoutShell({ children }: { children: React.ReactNode }) {
  const [collapsed] = useSidebarCollapsed();
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  // ui-future world (/future) renders its own chrome — no classic sidebar/shell.
  // Isolation law: the two worlds never share chrome (docs/UI-FUTURE-IMPLEMENTATION-PLAN.md).
  if (pathname?.startsWith('/future')) return <>{children}</>;

  return (
    <MobileNavContext.Provider value={{ open, setOpen }}>
      <div className="flex min-h-screen">
        <Sidebar />
        {/* Mobile: hamburger + scrim */}
        <button
          onClick={() => setOpen(true)}
          className="lg:hidden fixed left-3 top-3 z-30 w-10 h-10 rounded-xl glass-chrome flex items-center justify-center text-gray-300"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        {open && (
          <div
            onClick={() => setOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/60"
          />
        )}
        <main className={`flex-1 h-screen overflow-y-auto overflow-x-hidden transition-[margin] duration-200 ml-0 pt-14 lg:pt-0 ${collapsed ? 'lg:ml-[5.5rem]' : 'lg:ml-[16.5rem]'}`}>
          {children}
        </main>
      </div>
    </MobileNavContext.Provider>
  )
}
